import { useCallback } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { USER_SEARCH_PARAMS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";

import { useGupsStatus } from "../../../hooks/use-gups.tsx";
import { useScreenManager } from "../../../hooks/use-screen-manager";
import {
    getCurrentUrlWithParams,
    useEligibleTeamTypeForJtbdLocalStorage,
    mapJTBDtoTemplateChoice
} from "../../../utils/utils";
import { checkboxValues } from "../constants";
import { messages } from "../messages";

const buttonWrapperStyle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    margin: "auto 0 0",
    // Ensure at least 24px gap above buttons even when content fills the modal
    padding: `${token("space.300")} 0 0`,
    border: "none",
    gap: token("space.100"),
    width: "100%",
    [media.below.sm]: {
        position: "fixed",
        insetInlineStart: 0,
        insetBlockEnd: "0",
        backgroundColor: wacToken("color.neutral.light0"),
        padding: "14px 18px",
        width: "100%",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "-1px 0px 2px 0px #00000040"
    }
});

const buttonCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});

const backButtonCss = css({
    outline: `1px solid ${token("color.border")}`,
    outlineOffset: "-1px",
    borderRadius: "3px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        paddingBlock: "6px !important",
        paddingInline: "12px !important",
        margin: "0 !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontFamily: wacToken("font.charlie.text") + " !important",
        fontSize: "14px !important",
        lineHeight: "1.5rem !important",
        fontWeight: `${token("font.weight.medium")} !important`,
        textAlign: "center",
        margin: "0 !important"
    }
});
const Buttons = ({
    checkboxes,
    teamType
}: {
    checkboxes: checkboxValues[];
    teamType?: string;
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { prevScreen, nextScreen } = useScreenManager();
    const intl = useIntl();
    const { addJtbdCallback } = useGupsStatus();
    const removeUrlParameter = useCallback((url: string, paramKey: string) => {
        const r = new URL(url);
        r.searchParams.delete(paramKey);
        return r.href;
    }, []);
    // Don't render the back button if team is set in localStorage
    const teamSetFromLocalStorage = useEligibleTeamTypeForJtbdLocalStorage();
    const BackButton = (
        <div css={backButtonCss}>
            <Button
                appearance="subtle"
                onClick={() => {
                    const updatedUrl = removeUrlParameter(
                        window.location.href,
                        USER_SEARCH_PARAMS.teamType
                    );
                    history.replaceState({}, "", updatedUrl);
                    prevScreen();
                }}
            >
                {intl.formatMessage({
                    id: messages.backButton.id,
                    defaultMessage: messages.backButton.defaultMessage
                })}
            </Button>
        </div>
    );
    return (
        <div css={buttonWrapperStyle}>
            {!teamSetFromLocalStorage ? BackButton : ""}
            <div css={buttonCss}>
                <Button
                    appearance="primary"
                    onClick={async () => {
                        const checkedCheckboxVals = checkboxes.filter(
                            (checkbox) => checkbox.isChecked
                        );
                        const checkedBoxes = checkedCheckboxVals.map(
                            (checkbox) => checkbox.index
                        );
                        const { template, persona } = mapJTBDtoTemplateChoice(
                            checkboxes,
                            teamType
                        );
                        const jtbd =
                            checkedBoxes.length > 0
                                ? checkedBoxes.join(",")
                                : "0";
                        const jtbdVals =
                            checkedCheckboxVals.length > 0
                                ? checkedCheckboxVals
                                      .map((checkbox) => checkbox.value)
                                      .join(",")
                                : "NONE";
                        createAnalyticsEvent({
                            actionSubject: "button",
                            action: "clicked",
                            actionSubjectId: "jtbdContinue",
                            attributes: {
                                jtbd,
                                jtbdVals,
                                templateId: template,
                                teamType
                            }
                        }).fire();

                        await addJtbdCallback(checkboxes, template);

                        const updatedUrl = getCurrentUrlWithParams({
                            jtbdTemplateOverride: template ?? "",
                            jtbd:
                                checkedBoxes.length > 0
                                    ? checkedBoxes.join()
                                    : "0",
                            jtbdPersona: persona ?? ""
                        });
                        history.replaceState({}, "", updatedUrl);
                        nextScreen();
                    }}
                >
                    {intl.formatMessage({
                        id: messages.continueButton.id,
                        defaultMessage: messages.continueButton.defaultMessage
                    })}
                </Button>
            </div>
        </div>
    );
};
export default Buttons;
